<template>
    <div class="add-charge-form">
        <div class="form-content">
            <div class="form-group">
                <label class="field-label">{{ t('invoices-establishment') }} <span class="required">*</span></label>
                <KvSelect
                    :options="placesOptions"
                    v-model="form.places_id"
                    :disabled="saving || loadingPlaces"
                    :placeholder="loadingPlaces ? t('invoices-loading') : t('invoices-select_establishment')"
                    variant="outlined"
                    filterable
                />
            </div>

            <div class="form-row-2">
                <div class="form-group">
                    <label class="field-label">{{ t('invoices-total_extra_value') }} <span class="required">*</span></label>
                    <KvInput
                        v-model="form.installment_value"
                        :money="true"
                        :currency="selectedPlaceCurrency"
                        :disabled="saving"
                        placeholder="0,00"
                        variant="outlined"
                    />
                    <p class="field-hint">{{ t('invoices-add_charge_hint') }}</p>
                </div>

                <div class="form-group">
                    <label class="field-label">{{ t('invoices-installments') }} <span class="required">*</span></label>
                    <KvInput
                        v-model.number="form.installments"
                        type="number"
                        :min="1"
                        :max="60"
                        placeholder="1"
                        :disabled="saving"
                        variant="outlined"
                    />
                </div>
            </div>

            <div class="form-group">
                <label class="field-label">{{ t('invoices-first_due_date_label') }} <span class="required">*</span></label>
                <KvDatePicker
                    v-model="form.first_due_date"
                    value-format="yyyy-MM-dd"
                    :disabled="saving"
                    :placeholder="t('invoices-select_date')"
                />
            </div>

            <div class="form-group">
                <label class="field-label">{{ t('invoices-description') }}</label>
                <KvTextarea
                    v-model="form.description"
                    :placeholder="t('invoices-describe_negotiation')"
                    :rows="3"
                    :disabled="saving"
                    variant="outlined"
                />
            </div>

            <div class="installments-preview" v-if="canPreview">
                <div class="preview-header">
                    <Icon icon-class="ui-list" size="2" class="mr-2" />
                    {{ t('invoices-installments_preview') }}
                </div>
                <div class="preview-table-wrapper">
                    <table class="preview-table">
                        <thead>
                            <tr>
                                <th>{{ t('invoices-installment') }}</th>
                                <th>{{ t('invoices-due_date') }}</th>
                                <th class="text-right">{{ t('invoices-value') }}</th>
                                <th>{{ t('invoices-status_label') }}</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr v-for="(date, idx) in installmentsDates" :key="idx">
                                <td>{{ idx + 1 }}/{{ form.installments }}</td>
                                <td>
                                    <KvDatePicker
                                        v-model="installmentsDates[idx]"
                                        variant="text"
                                        size="small"
                                        value-format="yyyy-MM-dd"
                                        class="preview-date-picker"
                                    />
                                </td>
                                <td class="text-right font-weight-500">{{ formatCurrency(getInstallmentValue(idx)) }}</td>
                                <td><span class="status-draft-badge">{{ t('invoices-status_draft') }}</span></td>
                            </tr>
                        </tbody>
                        <tfoot>
                            <tr class="total-row">
                                <td colspan="2" class="text-right font-weight-500">{{ t('invoices-installments_sum') }}</td>
                                <td class="text-right font-weight-500">{{ formatCurrency(contractTotal) }}</td>
                                <td></td>
                            </tr>
                        </tfoot>
                    </table>
                </div>
            </div>

            <div class="error-alert" v-if="errorMsg">
                <Icon icon-class="ui-alert-triangle" size="2" class="mr-2" />
                {{ errorMsg }}
            </div>
        </div>

        <div class="form-actions">
            <kv-button variant="outlined" @click="$emit('cancel')" :disabled="saving">{{ t('invoices-cancel') }}</kv-button>
            <kv-button
                variant="filled"
                :prepend-icon="saving ? 'ui-loading-01' : 'ui-check'"
                @click="submit"
                :disabled="saving || !isValid"
            >
                {{ saving ? t('invoices-creating_invoices') : (form.installments > 1 ? t('invoices-create_n_invoices').replace('{n}', form.installments) : t('invoices-create_one_invoice')) }}
            </kv-button>
        </div>
    </div>
</template>

<script>
import { DateTime } from 'luxon';
import keviApi from '../../keviApi';
import { formatCurrency, extractApiError } from '../helpers';
import KvSelect from '../../components/inputs/kv-select.vue';
import KvInput from '../../components/inputs/kv-input.vue';
import KvTextarea from '../../components/inputs/kv-textarea.vue';
import Icon from '../../components/icon/icon.vue';

export default {
    name: 'add-charge-form',
    components: { KvSelect, KvInput, KvTextarea, Icon },
    props: {
    },
    data() {
        return {
            places: [],
            loadingPlaces: false,
            saving: false,
            errorMsg: null,
            installmentsDates: [],
            form: {
                places_id:      '',
                installment_value: null,
                installments:   1,
                first_due_date: DateTime.now().plus({ months: 1 }).set({ day: 5 }).toISODate(),
                description:    ''
            }
        };
    },
    created() {
        this.loadPlaces();
        this.updateDefaultDates();
    },
    computed: {
        today() {
            return DateTime.now().toISODate();
        },
        placesOptions() {
            return (this.places || []).map(p => ({ value: p.id, label: p.name }));
        },
        selectedPlaceCurrency() {
            const place = (this.places || []).find(p => p.id === this.form.places_id);
            return place?.currency || 'BRL';
        },
        canPreview() {
            return this.form.installment_value > 0
                && this.form.installments >= 1
                && this.form.first_due_date;
        },
        contractTotal() {
            const installment = parseFloat(this.form.installment_value) || 0;
            const count = Math.max(1, parseInt(this.form.installments, 10) || 1);
            return Math.round(installment * count * 100) / 100;
        },
        isValid() {
            return this.form.places_id
                && this.form.installment_value > 0
                && this.form.installments >= 1
                && this.form.first_due_date;
        }
    },
    watch: {
        'form.installments': 'updateDefaultDates',
        'form.first_due_date': 'updateDefaultDates',
        'form.installment_value': 'updateDefaultDates'
    },
    methods: {
        async loadPlaces() {
            if (this.places.length) return;
            this.loadingPlaces = true;
            try {
                const res = await this.$http(keviApi.getOptions('places', 'GET'));
                if (res && res.status === 200 && Array.isArray(res.body)) {
                    this.places = res.body;
                }
            } catch (e) {
                console.error('Erro ao carregar estabelecimentos:', e);
            } finally {
                this.loadingPlaces = false;
            }
        },
        updateDefaultDates() {
            if (!this.canPreview) {
                this.installmentsDates = [];
                return;
            }
            const n = Math.max(1, Math.min(60, this.form.installments || 1));
            const base = DateTime.fromISO(this.form.first_due_date);

            if (this.installmentsDates.length === n && this.installmentsDates[0] === this.form.first_due_date) {
                return;
            }

            const newDates = [];
            for (let i = 0; i < n; i++) {
                newDates.push(base.plus({ months: i }).toISODate());
            }
            this.installmentsDates = newDates;
        },
        getInstallmentValue() {
            return Math.round((parseFloat(this.form.installment_value) || 0) * 100) / 100;
        },
        async submit() {
            if (!this.isValid || this.saving) return;
            this.saving   = true;
            this.errorMsg = null;

            try {
                const payload = {
                    places_id:         this.form.places_id,
                    installment_value: parseFloat(this.form.installment_value),
                    installments:      parseInt(this.form.installments, 10),
                    first_due_date:    this.form.first_due_date,
                    custom_dates:      this.installmentsDates,
                    description:       this.form.description.trim()
                };

                const res = await this.$http(keviApi.getOptions('invoices/custom-charge', 'POST', payload));

                if (res.status === 201 && res.body && res.body.status === 'success') {
                    const count = res.body.created_count || 1;
                    this.$store.dispatch('notification/success',
                        this.t('invoices-charge_created_success').replace('{count}', count)
                    );
                    this.$emit('success', res.body.invoice_ids);
                } else {
                    this.errorMsg = extractApiError(res, this.t('invoices-create_charge_error'));
                }
            } catch (e) {
                this.errorMsg = extractApiError(e, this.t('invoices-connection_failure'));
            } finally {
                this.saving = false;
            }
        },
        reset() {
            this.form = {
                places_id:      '',
                installment_value: null,
                installments:   1,
                first_due_date: DateTime.now().plus({ months: 1 }).set({ day: 5 }).toISODate(),
                description:    ''
            };
            this.errorMsg = null;
            this.installmentsDates = [];
            this.saving = false;
        },
        formatCurrency
    }
};
</script>

<style lang="scss" scoped>
.add-charge-form {
    display: flex;
    flex-direction: column;
    gap: 24px;
}

.form-content {
    display: flex;
    flex-direction: column;
    gap: 18px;
}

.form-actions {
    display: flex;
    justify-content: flex-end;
    gap: 12px;
    margin-top: 8px;
}

.form-row-2 {
    display: grid;
    grid-template-columns: 1fr 160px;
    gap: 16px;
}

.form-group {
    display: flex;
    flex-direction: column;
    gap: 6px;
}

.field-label {
    font-size: 13px;
    font-weight: 500;
    color: #374151;
    .required { color: #EF4444; margin-left: 2px; }
}

.field-hint {
    font-size: 11px;
    color: #9CA3AF;
    margin: 2px 0 0;
}

.preview-date-picker {
    :deep(.kv-input-container) {
        border-color: transparent;
        background: transparent;
        padding: 0;

        .kv-native-input {
            padding: 4px 0;
            font-size: 13px;
        }

        .kv-input-icon {
            display: none;
        }
    }
}
.installments-preview {
    background: #F9FAFB;
    border: 1px solid #E5E7EB;
    border-radius: 12px;
    overflow: hidden;

    .preview-header {
        display: flex;
        align-items: center;
        padding: 12px 16px;
        font-size: 13px;
        font-weight: 500;
        color: #4B5563;
        text-transform: uppercase;
        letter-spacing: 0.05em;
        border-bottom: 1px solid #E5E7EB;
        background: #F3F4F6;
    }

    .preview-table-wrapper {
        max-height: 220px;
        overflow-y: auto;

        &::-webkit-scrollbar { width: 6px; }
        &::-webkit-scrollbar-track { background: #f1f1f1; }
        &::-webkit-scrollbar-thumb { background: #c1c1c1; border-radius: 4px; }
        &::-webkit-scrollbar-thumb:hover { background: #a8a8a8; }
    }

    .preview-table {
        width: 100%;
        border-collapse: collapse;
        font-size: 13px;

        th {
            padding: 8px 12px;
            color: #6B7280;
            font-weight: 500;
            font-size: 11px;
            text-transform: uppercase;
            letter-spacing: 0.04em;
            text-align: left;
            border-bottom: 1px solid #E5E7EB;
        }

        td {
            padding: 8px 12px;
            border-bottom: 1px solid #F3F4F6;
            color: #374151;
        }
        tr:last-child td { border-bottom: none; }

        .total-row td {
            padding: 10px 12px;
            background: #F3F4F6;
            border-top: 1px solid #E5E7EB;
            font-size: 13px;
        }
    }
}

.status-draft-badge {
    display: inline-flex;
    align-items: center;
    background: #F3F4F6;
    color: #4B5563;
    border: 1px solid #E5E7EB;
    border-radius: 100px;
    font-size: 11px;
    font-weight: 500;
    padding: 2px 8px;
}

.error-alert {
    background: #FEF2F2;
    border: 1px solid #FECACA;
    border-radius: 10px;
    padding: 12px 16px;
    font-size: 13px;
    color: #DC2626;
    display: flex;
    align-items: flex-start;
    gap: 8px;
}
</style>
